import type { Item, Scene, Team } from "./types";

/** Resolve the team object for an item, if any. */
export function teamFor(item: Item, scene: Scene): Team | undefined {
  if (!item.teamId) return undefined;
  return scene.teams?.find((t) => t.id === item.teamId);
}

/** True when an item participates in allocation (cabins, chairs, lounge sofas). */
export function isAllocatable(item: Item): boolean {
  if (item.kind === "room") return true;
  if (item.shape === "chair" || item.shape === "sofa") return true;
  return false;
}

/**
 * Compute an auto-numbered seat code for a chair, scoped to its cluster.
 * Within a cluster (groupId) chairs are ordered top-to-bottom, left-to-right
 * and labelled `<PREFIX>-01`, `<PREFIX>-02`, … The prefix is derived from the
 * cluster's room label (first two letters, uppercase) or `WS` if free-floating.
 * Returns the explicit `seatCode` when the user has overridden it.
 */
export function seatCodeFor(item: Item, scene: Scene): string | undefined {
  if (item.shape !== "chair") return undefined;
  if (item.seatCode) return item.seatCode;
  const groupId = item.groupId;
  const peers = groupId
    ? scene.items.filter((i) => i.groupId === groupId && i.shape === "chair")
    : scene.items.filter((i) => !i.groupId && i.shape === "chair");
  const ordered = [...peers].sort((a, b) => (a.y - b.y) || (a.x - b.x));
  const index = ordered.findIndex((c) => c.id === item.id);
  if (index < 0) return undefined;
  let prefix = "WS";
  if (groupId) {
    const room = scene.items.find((i) => i.groupId === groupId && i.kind === "room");
    if (room?.label) {
      const letters = room.label.replace(/[^A-Za-z]/g, "").toUpperCase();
      if (letters.length >= 2) prefix = letters.slice(0, 2);
      else if (letters.length === 1) prefix = letters + "S";
    }
  }
  return `${prefix}-${String(index + 1).padStart(2, "0")}`;
}